<script setup>
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import CloserQuoteSlideout from './CloserQuoteSlideout.vue';

const maxResults = 8;

const leads = ref([]);
const loading = ref(true);
const loadError = ref(false);
const searchTerm = ref('');
const resultsVisible = ref(false);
const highlightedIndex = ref(0);
const selectedLead = ref(null);
let searchTimer = null;
let searchRequestId = 0;

const loadLeads = async () => {
    const requestId = ++searchRequestId;
    loading.value = true;
    loadError.value = false;

    try {
        const res = await axios.get(route('crm.leadsContractSearch'), {
            params: {fuzzy: searchTerm.value, limit: maxResults},
        });

        if (requestId !== searchRequestId) {
            return;
        }

        leads.value = res.data;
    } catch (e) {
        if (requestId === searchRequestId) {
            loadError.value = true;
        }
    } finally {
        if (requestId === searchRequestId) {
            loading.value = false;
        }
    }
};

onMounted(loadLeads);
onBeforeUnmount(() => clearTimeout(searchTimer));

const filteredLeads = computed(() => leads.value.slice(0, maxResults));

const domainForLead = (lead) => {
    if (lead.website) {
        try {
            const url = lead.website.includes('://') ? lead.website : 'https://' + lead.website;
            return new URL(url).hostname.replace(/^www\./, '');
        } catch {
            return lead.website.replace(/^www\./, '');
        }
    }
    if (lead.email) {
        const parts = lead.email.split('@');
        if (parts.length === 2) {
            return parts[1];
        }
    }
    return null;
};

const selectLead = (lead) => {
    selectedLead.value = lead;
    resultsVisible.value = false;
};

const handleInput = () => {
    clearTimeout(searchTimer);
    searchRequestId += 1;
    highlightedIndex.value = 0;
    resultsVisible.value = true;
    searchTimer = setTimeout(loadLeads, 250);
};

const handleKeydown = (e) => {
    if (e.key === 'ArrowDown') {
        e.preventDefault();
        resultsVisible.value = true;
        highlightedIndex.value = Math.min(highlightedIndex.value + 1, filteredLeads.value.length - 1);
    } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        highlightedIndex.value = Math.max(highlightedIndex.value - 1, 0);
    } else if (e.key === 'Enter') {
        e.preventDefault();
        const lead = filteredLeads.value[highlightedIndex.value];
        if (lead) {
            selectLead(lead);
        }
    } else if (e.key === 'Escape') {
        resultsVisible.value = false;
    }
};
</script>

<template>
    <div class="flex flex-col items-center grow py-16">
        <div class="flex flex-col items-center grow">
            <div class="flex items-center justify-center w-16 h-16 mb-6 rounded-full bg-aiAgentSalesAssistant">
                <i class="text-xl text-gray-800 fa-regular fa-file-signature"></i>
            </div>
            <h2 class="text-lg font-semibold text-gray-800 mb-2">
                <mf-translate domainkey="crm:nina.standalone.quote.title"></mf-translate>
            </h2>
            <p class="text-sm text-gray-500 mb-6 text-center max-w-md">
                <mf-translate domainkey="crm:nina.standalone.quote.description"></mf-translate>
            </p>
        </div>

        <div class="relative w-full max-w-md">
            <input
                v-model="searchTerm"
                type="search"
                autocomplete="off"
                class="w-full px-4 py-2.5 text-sm border border-gray-200 rounded-lg"
                :placeholder="_mft('crm:nina.standalone.quote.placeholder')"
                @focus="resultsVisible = true"
                @blur="resultsVisible = false"
                @input="handleInput"
                @keydown="handleKeydown"
            />

            <div
                v-if="resultsVisible"
                class="absolute z-10 w-full mt-1 overflow-y-auto bg-white border border-gray-200 rounded-lg shadow-lg max-h-64"
            >
                <div v-if="loading" class="flex items-center justify-center py-4">
                    <mf-spinner-medium class="w-16 h-8" />
                </div>

                <p v-else-if="loadError" class="px-4 py-3 mb-0 text-sm text-gray-500">
                    <mf-translate domainkey="crm:nina.standalone.quote.loadError"></mf-translate>
                </p>

                <p v-else-if="filteredLeads.length === 0" class="px-4 py-3 mb-0 text-sm text-gray-500">
                    <mf-translate domainkey="crm:nina.standalone.quote.noResults"></mf-translate>
                </p>

                <div v-else>
                    <button
                        v-for="(lead, index) in filteredLeads"
                        :key="lead.id"
                        type="button"
                        class="block w-full px-4 py-2 text-left"
                        :class="index === highlightedIndex ? 'bg-gray-100' : 'hover:bg-gray-50'"
                        @mousedown.prevent="selectLead(lead)"
                        @mouseenter="highlightedIndex = index"
                    >
                        <span class="block text-sm text-gray-900">
                            {{ lead.name }}<span v-if="lead.company" class="text-gray-500"> &middot; {{ lead.company }}</span>
                        </span>
                        <span v-if="lead.email" class="block text-xs text-gray-400">{{ lead.email }}</span>
                    </button>
                </div>
            </div>
        </div>
    </div>

    <closer-quote-slideout
        v-if="selectedLead"
        :lead-id="selectedLead.id"
        :lead-name="selectedLead.name"
        :lead-email="selectedLead.email"
        :lead-company="selectedLead.company"
        :domain="domainForLead(selectedLead)"
        @close="selectedLead = null"
    />
</template>
